import http from "../../lib/http";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import caroselImage from "../../assets/images/caroselImage.png";
import productImage from "../../assets/images/product.png";
import hotOfferImg from "../../assets/images/todays_hot_offer_image.png";
import { MobileAppDownload } from "../common";

import {
  getTopVouchers,
  applyVoucher,
  getAppliedVouchers,
} from "../../services/vouchers";
import { getCategories } from "../../services/catalog";
import { getBestSellingProducts } from "../../services/products";
import {
  addToWishlist,
  removeFromWishlist,
  checkWishlistStatus,
} from "../../services/wishlist";
import {
  getPromoCodes,
  applyPromoCode,
  getAppliedPromoCodes,
} from "../../services/promoCodes";
import FlashSale from "../FlashSale/FlashSale";
import { toast } from "react-toastify";
import "../../styles/HeroSection.css";
import { getBanners } from "../../services/banner";

const AUTOPLAY_MS = 4000;

// ── Icons ─────────────────────────────────────────────
const ChevronLeft = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

const ChevronRight = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

const HeartIcon = ({ filled }) => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill={filled ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
  </svg>
);

const CopyIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="9" width="13" height="13" rx="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

const TagIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
    <line x1="7" y1="7" x2="7.01" y2="7" />
  </svg>
);

const PercentIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="19" y1="5" x2="5" y2="19" />
    <circle cx="6.5" cy="6.5" r="2.5" />
    <circle cx="17.5" cy="17.5" r="2.5" />
  </svg>
);

const GridIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="7" height="7" />
    <rect x="14" y="3" width="7" height="7" />
    <rect x="14" y="14" width="7" height="7" />
    <rect x="3" y="14" width="7" height="7" />
  </svg>
);

const ArrowRightIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);

const BoltIcon = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor" stroke="none">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
  </svg>
);

// ── Price helpers ──────────────────────────────────────
const normalizeUrl = (u) =>
  typeof u === "string" && u.startsWith("http://")
    ? u.replace("http://", "https://")
    : u;

const resolvePrice = (p) => {
  const finalPrice    = Number(p.final_price             ?? 0);
  const flashPrice    = Number(p.flash_sale_price        ?? 0);
  const discountPrice = Number(p.product_discount_price  ?? 0);
  const regularPrice  = Number(p.price                   ?? 0);

  if (finalPrice    > 0) return finalPrice;
  if (flashPrice    > 0) return flashPrice;
  if (discountPrice > 0) return discountPrice;
  return regularPrice;
};

const resolveOriginalPrice = (p) => Number(p.price ?? 0);

const resolvePricingLabel = (p) => {
  if (p.is_flash_sale || p.pricing_type === "flash_sale") return "flash";
  if (p.pricing_type === "discount" || p.product_discount_price) return "discount";
  return null;
};

const resolveProductImage = (p, fallback) => {
  if (Array.isArray(p.product_images) && p.product_images.length > 0) {
    const url = p.product_images[0];
    if (typeof url === "string" && url.trim()) return normalizeUrl(url);
  }
  const raw = p.image_url || p.thumbnail || "";
  if (raw) return normalizeUrl(raw);
  return fallback;
};

// ── Component ──────────────────────────────────────────
const HeroSection = ({ selectedCategory }) => {
  const navigate = useNavigate();
  const categoriesGridRef = useRef(null);

  const authToken       = localStorage.getItem("authToken");
  const isAuthenticated = !!authToken;

  const [vouchers,      setVouchers]      = useState([]);
  const [categories,    setCategories]    = useState([]);
  const [promoCodes,    setPromoCodes]    = useState([]);
  const [bestProducts,  setBestProducts]  = useState([]);
  const [wishlistItems, setWishlistItems] = useState(new Set());

  const [loadingVouchers,   setLoadingVouchers]   = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingPromos,     setLoadingPromos]     = useState(false);
  const [loadingBest,       setLoadingBest]       = useState(false);
  const [loadingVoucherIds, setLoadingVoucherIds] = useState(new Set());
  const [loadingPromoIds,   setLoadingPromoIds]   = useState(new Set());
  const [collectedVoucherIds, setCollectedVoucherIds] = useState(new Set());
  const [appliedPromoIds,     setAppliedPromoIds]     = useState(new Set());

  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPaused,     setIsPaused]     = useState(false);

  const [banners,        setBanners]        = useState([]);
  const [loadingBanners, setLoadingBanners] = useState(false);
  const totalSlides = banners.length || 1;

  // ── Derived: what to show in the offers strip ──────
  const offersStillLoading = loadingVouchers || loadingPromos;
  const hasVouchers        = vouchers.length > 0;
  const hasPromos          = promoCodes.length > 0;
  const showOffersStrip    = !offersStillLoading && (hasVouchers || hasPromos);
  const showVoucherCol     = hasVouchers;
  const showPromoCol       = hasPromos;
  const showDivider        = showVoucherCol && showPromoCol;

  const handleProductClick = (payload) => {
    const slug =
      typeof payload === "object"
        ? payload?.slug
        : String(payload ?? "").trim();
    if (!slug) { toast.error("Product link not available"); return; }
    navigate(`/product/${slug}`);
  };

  const goToSlide = (i) => setCurrentSlide(i);
  const goToNext  = () => setCurrentSlide((p) => (p + 1) % totalSlides);
  const goToPrev  = () => setCurrentSlide((p) => (p - 1 + totalSlides) % totalSlides);

  const getApiMessage = (err, fb = "Something went wrong") =>
    err?.response?.data?.message ||
    err?.response?.data?.error  ||
    err?.message ||
    fb;

  const handleWishlistToggle = async (e, productId) => {
    e.stopPropagation();
    try {
      if (wishlistItems.has(productId)) {
        const item = await checkWishlistStatus(productId);
        if (item) {
          await removeFromWishlist(item.id);
          setWishlistItems((p) => { const n = new Set(p); n.delete(productId); return n; });
          toast.success("Removed from wishlist");
        }
      } else {
        await addToWishlist(productId);
        setWishlistItems((p) => new Set([...p, productId]));
        toast.success("Added to wishlist ♥");
      }
    } catch (err) {
      if (err.response?.status !== 401) toast.error("Failed to update wishlist");
    }
  };

  const handleCategoryClick = (cat) => {
    if (cat.name === "All") { navigate("/explore"); return; }
    navigate("/explore", {
      state: {
        selectedCategory: {
          id:            cat.id,
          name:          cat.name,
          category_name: cat.name,
          icon:          cat.icon,
          image:         cat.image,
        },
      },
    });
  };

  // Banners
  useEffect(() => {
    let mounted = true;
    const loadBanners = async () => {
      setLoadingBanners(true);
      try {
        const { data } = await getBanners();
        const raw    = data?.data || [];
        const mapped = Array.isArray(raw)
          ? raw.map((item, index) => ({
              id:          item.id ?? index,
              banner_name: item.banner_name || `Banner ${index + 1}`,
              image_url:   normalizeUrl(item.image_url) || caroselImage,
            }))
          : [];
        if (mounted) { setBanners(mapped); setCurrentSlide(0); }
      } catch {
        if (mounted) setBanners([]);
      } finally {
        if (mounted) setLoadingBanners(false);
      }
    };
    loadBanners();
    return () => { mounted = false; };
  }, []);

  // Auto-advance carousel (pauses on hover; restarts after manual navigation
  // so the progress bar and the timer always stay in sync)
  useEffect(() => {
    if (banners.length <= 1 || isPaused) return;
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % banners.length);
    }, AUTOPLAY_MS);
    return () => clearInterval(interval);
  }, [banners, isPaused, currentSlide]);

  // Vouchers, categories, promos
  useEffect(() => {
    let mounted = true;

    const loadVouchers = async () => {
      if (!isAuthenticated) return;
      setLoadingVouchers(true);
      try {
        const { data } = await getTopVouchers();
        const raw = (data?.data || data || []).filter(
          (x) => x?.is_active === true || String(x?.is_active) === "1",
        );
        if (mounted) {
          setVouchers(
            raw.map((v) => {
              const type  = String(v.type || "").toLowerCase();
              const value = Number(v.value ?? 0);
              return {
                id:            v.id,
                code:          v.code,
                type,
                value,
                minSpend:      Number(v.min_purchase ?? 0),
                validFrom:     v.valid_from,
                validUntil:    v.valid_until,
                collected:     !!v.collected,
                discountLabel:
                  type === "fixed" ? `$${value.toFixed(0)} OFF` : `${value}% OFF`,
              };
            }),
          );
        }
      } catch {
        if (mounted) setVouchers([]);
      } finally {
        if (mounted) setLoadingVouchers(false);
      }
    };

    const loadCollected = async () => {
      if (!isAuthenticated) return;
      try {
        const res = await getAppliedVouchers?.();
        const raw = res?.data?.data || res?.data || res || [];
        const ids = (Array.isArray(raw) ? raw : [])
          .map((x) => x.voucher_id ?? x.id ?? x.voucher?.id)
          .filter(Boolean);
        if (mounted) setCollectedVoucherIds(new Set(ids));
      } catch {}
    };

    const loadCategories = async () => {
      setLoadingCategories(true);
      try {
        const { data } = await getCategories();
        const raw    = data?.data || data || [];
        const mapped = raw.map((c, i) => ({
          id:    String(c.id ?? i),
          name:  c.category_name || `Category ${i + 1}`,
          icon:  "🛍️",
          image: c.category_image || null,
        }));
        if (mounted) {
          setCategories([
            { id: "all", name: "All", icon: "🛍️", image: null },
            ...mapped,
          ]);
        }
      } catch {
        if (mounted) setCategories([{ id: "all", name: "All", icon: "🛍️", image: null }]);
      } finally {
        if (mounted) setLoadingCategories(false);
      }
    };

    const loadPromos = async () => {
      if (!isAuthenticated) return;
      setLoadingPromos(true);
      try {
        const res = await getPromoCodes();
        const raw = Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : [];
        if (mounted) {
          setPromoCodes(
            raw
              .filter((p) => String(p.is_active) === "1" || p.is_active === true)
              .map((p) => ({
                id:        p.id,
                code:      p.code,
                type:      p.type,
                value:     Number(p.value ?? 0),
                minOrder:  Number(p.min_order_amount ?? 0),
                startDate: p.start_date,
                endDate:   p.end_date,
              })),
          );
        }
      } catch {
        if (mounted) setPromoCodes([]);
      } finally {
        if (mounted) setLoadingPromos(false);
      }
    };

    const loadAppliedPromos = async () => {
      if (!isAuthenticated) return;
      try {
        const res = await getAppliedPromoCodes?.();
        const raw = res?.data?.data || res?.data || res || [];
        const ids = (Array.isArray(raw) ? raw : [])
          .map((x) => x.promocode_id ?? x.id ?? x.promo_code_id ?? x.promo?.id)
          .filter(Boolean);
        if (mounted) setAppliedPromoIds(new Set(ids));
      } catch {}
    };

    loadCategories();
    if (isAuthenticated) {
      loadVouchers();
      loadCollected();
      loadPromos();
      loadAppliedPromos();
    } else {
      if (mounted) {
        setVouchers([]);
        setPromoCodes([]);
        setCollectedVoucherIds(new Set());
        setAppliedPromoIds(new Set());
      }
    }
    return () => { mounted = false; };
  }, [isAuthenticated]);

  // Best-selling products
  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoadingBest(true);
      try {
        const { data } = await getBestSellingProducts({ limit: 30 });
        const raw  = data?.data || [];
        const list = raw.slice(0, 30).map((p, i) => ({
          id:            p.id ?? i,
          slug:          p.slug,
          image:         resolveProductImage(p, productImage),
          title:         p.name || p.title || "Product",
          price:         resolvePrice(p),
          originalPrice: resolveOriginalPrice(p),
          pricingLabel:  resolvePricingLabel(p),
          isFlashSale:   !!p.is_flash_sale,
          rating:        Number(p.average_rating ?? p.rating ?? 0),
          reviews:       Number(p.total_reviews  ?? p.review_count ?? 0),
          soldCount:     Number(p.sold_count      ?? p.units_sold  ?? 0),
          categoryName:  p.category?.category_name ?? "",
          categoryId:    p.category?.id            ?? null,
        }));
        if (mounted) setBestProducts(list);
      } catch {
        if (mounted) setBestProducts([]);
      } finally {
        if (mounted) setLoadingBest(false);
      }
    };
    load();
    return () => { mounted = false; };
  }, []);

  // Wishlist seed
  useEffect(() => {
    if (!isAuthenticated) { setWishlistItems(new Set()); return; }
    (async () => {
      try {
        const { data } = await http.get("/api/customer/wishlists");
        const items = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
        setWishlistItems(
          new Set(items.map((i) => i.product_id || i.product?.id).filter(Boolean)),
        );
      } catch {}
    })();
  }, [isAuthenticated]);

  const handleCollectVoucher = async (id) => {
    setLoadingVoucherIds((p) => new Set(p).add(id));
    setCollectedVoucherIds((p) => new Set(p).add(id));
    setVouchers((p) => p.map((v) => (v.id === id ? { ...v, collected: true } : v)));
    try {
      await applyVoucher(id);
      toast.success("Voucher collected! 🎉");
    } catch (err) {
      toast.error(getApiMessage(err, "Failed to collect voucher."));
      setCollectedVoucherIds((p) => { const n = new Set(p); n.delete(id); return n; });
      setVouchers((p) => p.map((v) => (v.id === id ? { ...v, collected: false } : v)));
    } finally {
      setLoadingVoucherIds((p) => { const n = new Set(p); n.delete(id); return n; });
    }
  };

  const handleApplyPromo = async (id) => {
    setLoadingPromoIds((p) => new Set(p).add(id));
    setAppliedPromoIds((p) => new Set(p).add(id));
    try {
      await applyPromoCode(id);
      toast.success("Promo applied! 🎉");
    } catch (err) {
      toast.error(getApiMessage(err, "Failed to apply promo."));
      setAppliedPromoIds((p) => { const n = new Set(p); n.delete(id); return n; });
    } finally {
      setLoadingPromoIds((p) => { const n = new Set(p); n.delete(id); return n; });
    }
  };

  const handleCopyCode = async (code) => {
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      toast.success("Code copied!");
    } catch {
      toast.error("Failed to copy");
    }
  };

  const formatPromoValue = (p) =>
    String(p.type).toLowerCase() === "amount"
      ? `$${Number(p.value).toFixed(2)} OFF`
      : `${Number(p.value)}% OFF`;

  const isCollected = (v) => !!v.collected || collectedVoucherIds.has(v.id);
  const isApplied   = (p) => appliedPromoIds.has(p.id);

  // ── Carousel JSX (shared by logged-in + guest views) ──
  const carouselInner = (guestMode = false) => {
    const slides =
      banners.length > 0
        ? banners
        : [{ id: "fallback", banner_name: "Default Banner", image_url: caroselImage }];

    return (
      <div
        className={`hs-carousel${guestMode ? " hs-carousel-guest" : ""}`}
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        {loadingBanners ? (
          <div className="hs-carousel-track">
            <div className={`hs-carousel-slide is-active${guestMode ? " hs-carousel-slide-guest" : ""}`}>
              <img src={caroselImage} alt="Loading banner" className="hs-carousel-img" />
              {guestMode && <div className="hs-guest-banner-overlay" />}
            </div>
          </div>
        ) : (
          <>
            <div
              className="hs-carousel-track"
              style={{ transform: `translateX(-${currentSlide * 100}%)` }}
            >
              {slides.map((banner, i) => (
                <div
                  key={banner.id}
                  className={`hs-carousel-slide${guestMode ? " hs-carousel-slide-guest" : ""}${i === currentSlide ? " is-active" : ""}`}
                >
                  <img
                    src={banner.image_url}
                    alt={banner.banner_name}
                    className="hs-carousel-img"
                    loading={i === 0 ? "eager" : "lazy"}
                    onError={(e) => { e.currentTarget.src = caroselImage; }}
                  />
                  {guestMode && <div className="hs-guest-banner-overlay" />}
                </div>
              ))}
            </div>

            {banners.length > 1 && (
              <>
                <button
                  className="hs-carousel-arrow hs-carousel-prev"
                  onClick={goToPrev}
                  aria-label="Previous banner"
                >
                  <ChevronLeft />
                </button>
                <button
                  className="hs-carousel-arrow hs-carousel-next"
                  onClick={goToNext}
                  aria-label="Next banner"
                >
                  <ChevronRight />
                </button>

                <div className="hs-carousel-dots">
                  {banners.map((_, i) => (
                    <button
                      key={i}
                      className={`hs-dot ${i === currentSlide ? "active" : ""}`}
                      onClick={() => goToSlide(i)}
                      aria-label={`Go to banner ${i + 1}`}
                    />
                  ))}
                </div>

                <div className="hs-carousel-counter">
                  {currentSlide + 1} / {banners.length}
                </div>

                {!isPaused && (
                  <div className="hs-carousel-progress">
                    <span
                      key={currentSlide}
                      style={{ animationDuration: `${AUTOPLAY_MS}ms` }}
                    />
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <section className="hs-root">
      <div className="hs-container">

        {isAuthenticated ? (
          <div className="hs-top">

            {/* ── Row 1: eyebrow left, subtext right ── */}
            <div className="hs-hero-header">
              <div className="hs-hero-left">
                <div className="hs-eyebrow">🌏 Easy World Online</div>
              </div>
              <div className="hs-hero-right">
                <p className="hs-hero-sub">Shop Everything, Delivered Fast.</p>
              </div>
            </div>

            {/* ── Row 2: Full-width carousel ── */}
            <div className="hs-carousel-wrap">
              {carouselInner(false)}
            </div>

            {/* ── Row 3: Horizontal offers strip ── */}
            {showOffersStrip && (
              <div className="hs-offers-strip">

                {/* Vouchers column */}
                {showVoucherCol && (
                  <div className="hs-offers-strip-col">
                    <div className="hs-offers-section-header">
                      <div className="hs-offers-section-title">
                        <TagIcon /> Available Vouchers
                      </div>
                      <button className="hs-inline-view-all" onClick={() => navigate("/vouchers")}>
                        View all →
                      </button>
                    </div>

                    <div className="hs-h-offers-row">
                      {vouchers.slice(0, 2).map((v) => {
                        const collected  = isCollected(v);
                        const collecting = loadingVoucherIds.has(v.id);
                        return (
                          <div key={v.id} className={`hs-h-ticket ${collected ? "is-used" : ""}`}>
                            <div className="hs-h-ticket-left">
                              <span className="hs-h-ticket-value">{v.discountLabel || "OFFER"}</span>
                              <span className="hs-h-ticket-type">
                                {v.type === "fixed" ? "Flat Discount" : "Percentage Off"}
                              </span>
                            </div>
                            <div className="hs-h-ticket-sep">
                              <span className="hs-h-notch top" />
                              <span className="hs-h-notch bottom" />
                            </div>
                            <div className="hs-h-ticket-right">
                              <div className="hs-h-code-row">
                                <span className="hs-h-code">{v.code || "VOUCHER"}</span>
                                {v.code && (
                                  <button className="hs-copy-btn" onClick={() => handleCopyCode(v.code)}>
                                    <CopyIcon />
                                  </button>
                                )}
                              </div>
                              <div className="hs-h-meta">
                                Min. <strong>${Number(v.minSpend || 0).toFixed(0)}</strong>
                                {v.validUntil && <span className="hs-h-expiry"> · {v.validUntil}</span>}
                              </div>
                              <button
                                className={`hs-h-btn ${collected ? "is-done" : ""}`}
                                onClick={() => !collected && handleCollectVoucher(v.id)}
                                disabled={collected || collecting}
                              >
                                {collected ? "Collected ✓" : collecting ? "…" : "Collect"}
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {showDivider && <div className="hs-offers-strip-divider" />}

                {/* Promo codes column */}
                {showPromoCol && (
                  <div className="hs-offers-strip-col">
                    <div className="hs-offers-section-header">
                      <div className="hs-offers-section-title">
                        <PercentIcon /> Promo Codes
                      </div>
                      <button className="hs-inline-view-all" onClick={() => navigate("/vouchers")}>
                        View all →
                      </button>
                    </div>

                    <div className="hs-promo-row">
                      {promoCodes.slice(0, 2).map((p) => {
                        const applied  = isApplied(p);
                        const applying = loadingPromoIds.has(p.id);
                        const typeLabel =
                          String(p.type).toLowerCase() === "amount" ? "Flat Discount" : "Percentage Off";
                        return (
                          <div key={p.id} className={`hs-promo-ticket ${applied ? "is-applied" : ""}`}>
                            <div className="hs-promo-ticket-left">
                              <span className="hs-promo-ticket-value">{formatPromoValue(p)}</span>
                              <span className="hs-promo-ticket-type">{typeLabel}</span>
                            </div>
                            <div className="hs-promo-ticket-sep">
                              <span className="hs-promo-notch top" />
                              <span className="hs-promo-notch bottom" />
                            </div>
                            <div className="hs-promo-ticket-right">
                              <div className="hs-promo-ticket-code-row">
                                <span className="hs-promo-ticket-code">{p.code}</span>
                                <button className="hs-copy-btn" onClick={() => handleCopyCode(p.code)}>
                                  <CopyIcon />
                                </button>
                              </div>
                              <div className="hs-promo-ticket-meta">
                                Min. <strong>${Number(p.minOrder || 0).toFixed(0)}</strong>
                                {p.endDate && <span> · Exp {p.endDate}</span>}
                              </div>
                              <button
                                className={`hs-promo-apply-btn ${applied ? "is-applied" : ""}`}
                                onClick={() => !applied && handleApplyPromo(p.id)}
                                disabled={applied || applying}
                              >
                                {applied ? "Applied ✓" : applying ? "…" : "Apply"}
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

              </div>
            )}
            {/* ── end offers strip ── */}

          </div>
        ) : (
          /* ── Guest / unauthenticated view ── */
          <div className="hs-top hs-top-guest-banner-only">
            <div className="hs-guest-banner-wrap">
              {carouselInner(true)}
            </div>
          </div>
        )}

        <FlashSale
          onProductClick={(payload) => {
            const slug = typeof payload === "object" ? payload?.slug : payload;
            handleProductClick(slug);
          }}
        />

        {/* ── Categories ── */}
        <div className="hs-categories">
          <div className="hs-categories-header">
            <div className="hs-categories-title"><GridIcon /> Browse Categories</div>
            <button className="hs-categories-all" onClick={() => navigate("/explore")}>See all →</button>
          </div>
          <div className="hs-cat-row" ref={categoriesGridRef}>
            {loadingCategories && (
              <div className="hs-offers-empty"><span className="hs-spinner" /> Loading…</div>
            )}
            {!loadingCategories && categories.map((cat) => (
              <button
                key={cat.id}
                className={`hs-cat-btn ${selectedCategory === cat.name ? "active" : ""}`}
                onClick={() => handleCategoryClick(cat)}
              >
                <span className="hs-cat-icon">
                  {cat.image
                    ? <img src={cat.image} alt={cat.name} className="hs-cat-img" />
                    : "🛍️"}
                </span>
                <span className="hs-cat-name">{cat.name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* ── Best products ── */}
        <div className="hs-section">
          <div className="hs-section-header">
            <div>
              <h2 className="hs-section-title">Browse Products</h2>
              <p className="hs-section-sub">Explore a wide range of products curated for you</p>
            </div>
            <button className="hs-view-all" onClick={() => navigate("/explore")}>
              View All <ArrowRightIcon />
            </button>
          </div>

          {loadingBest && (
            <div className="hs-offers-empty hs-offers-empty-lg">
              <span className="hs-spinner hs-spinner-lg" /> Loading products…
            </div>
          )}
          {!loadingBest && bestProducts.length === 0 && (
            <div className="hs-offers-empty">No products available.</div>
          )}

          <div className="hs-products-grid">
            {!loadingBest && bestProducts.map((p, i) => {
              const hasDiscount = p.originalPrice > p.price && p.originalPrice > 0;
              const discountPct = hasDiscount
                ? Math.round(((p.originalPrice - p.price) / p.originalPrice) * 100)
                : 0;
              return (
                <div
                  key={p.id}
                  className="hs-product-card"
                  style={{ animationDelay: `${i * 40}ms` }}
                  onClick={() => handleProductClick(p.slug)}
                >
                  <div className="hs-product-img-wrap">
                    <img
                      src={p.image}
                      alt={p.title}
                      className="hs-product-img"
                      onError={(e) => { e.currentTarget.src = productImage; }}
                    />
                    {p.isFlashSale && (
                      <span className="hs-product-badge hs-badge-flash">
                        <BoltIcon /> Flash
                      </span>
                    )}
                    {!p.isFlashSale && hasDiscount && discountPct > 0 && (
                      <span className="hs-product-badge">-{discountPct}%</span>
                    )}
                    <button
                      className={`hs-product-heart ${wishlistItems.has(p.id) ? "active" : ""}`}
                      onClick={(e) => handleWishlistToggle(e, p.id)}
                    >
                      <HeartIcon filled={wishlistItems.has(p.id)} />
                    </button>
                  </div>
                  <div className="hs-product-body">
                    <div className="hs-product-title">{p.title}</div>
                    <div className="hs-product-price-row">
                      <span className={`hs-product-price ${p.isFlashSale ? "hs-price-flash" : ""}`}>
                        ${Number(p.price || 0).toFixed(2)}
                      </span>
                      {hasDiscount && (
                        <span className="hs-product-original">${Number(p.originalPrice).toFixed(2)}</span>
                      )}
                    </div>
                    {p.rating > 0 && (
                      <div className="hs-product-rating">
                        <span className="hs-star">⭐</span>
                        <span className="hs-rating-text">
                          {p.rating.toFixed(1)}
                          {p.reviews > 0 && <span className="hs-rating-count"> ({p.reviews})</span>}
                        </span>
                      </div>
                    )}
                    {p.soldCount > 0 && (
                      <div className="hs-product-sold">🛒 {p.soldCount} sold</div>
                    )}
                    <button
                      className="hs-product-cta"
                      onClick={(e) => { e.stopPropagation(); handleProductClick(p.slug); }}
                    >
                      View Product
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Hot offer banner ── */}
        <div className="hs-hot-offer" style={{ backgroundImage: `url(${hotOfferImg})` }}>
          <div className="hs-hot-offer-overlay" />
          <div className="hs-hot-offer-content">
            <div className="hs-hot-badge">🔥 Today Only</div>
            <h3 className="hs-hot-title">Today's Hot Offer</h3>
            <p className="hs-hot-text">
              Flat 25% discount on your first 50 orders. Limited time — grab it now!
            </p>
            <button className="hs-hot-btn" onClick={() => navigate("/explore")}>
              Shop Now <ArrowRightIcon />
            </button>
          </div>
        </div>

      </div>

      <MobileAppDownload />
    </section>
  );
};

export default HeroSection;